import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, MapPin, Phone, Wrench } from "lucide-react";
import { SEO } from "@/components/SEO";
import { CITIES } from "@/data/citiesData";
import { PANNES } from "@/data/pannesData";
import {
  ALL_COMBOS,
  getPanneBaseSlug,
  buildComboSlug,
} from "@/data/panneVilleData";

export default function ReparationsLocalesPage() {
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Pages réparations locales Mediastore Niort",
    description: `${ALL_COMBOS.length} combinaisons panne × ville couvrant la Deux-Sèvres et l'ouest dans un rayon de 80 km`,
    numberOfItems: ALL_COMBOS.length,
  };

  return (
    <div className="min-h-screen" data-testid="reparations-locales-page">
      <SEO
        title={`Réparations locales - ${ALL_COMBOS.length} pages par ville & panne | Mediastore Niort`}
        description={`${ALL_COMBOS.length} pages réparations dédiées : ${PANNES.length} pannes pour ${CITIES.length} villes dans un rayon de 80 km de Niort. Trouvez la page adaptée à votre ville et votre panne (écran cassé, batterie, désoxydation, MacBook, PS5, données…). Diagnostic gratuit, garantie 6 mois.`}
        keywords={`réparation locale Niort, réparation iPhone Chauray, réparation iPhone Aiffres, réparation Mac La Rochelle, réparation iPhone Poitiers, réparation Apple Bressuire, réparation iPhone Parthenay, Mediastore villes pannes`}
        path="/reparations-locales"
        schema={itemListSchema}
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Réparations locales", path: "/reparations-locales" },
        ]}
      />

      {/* HERO */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-red-900 text-white">
        <div className="container-custom text-center">
          <Badge className="bg-white/10 text-white border border-white/20 px-4 py-2 mb-6 hover:bg-white/10">
            <Wrench className="w-4 h-4 mr-1" />
            {ALL_COMBOS.length} pages dédiées (12 pannes × 18 villes)
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Réparations locales — chaque panne, chaque ville
          </h1>
          <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
            Sélectionnez votre <strong className="text-white">ville</strong> ci-dessous pour voir
            les <strong className="text-red-400">12 pannes</strong> traitées avec trajet, route
            d&apos;accès, distance et tarifs détaillés. Diagnostic gratuit, garantie 6 mois,
            intervention en 30 min sur RDV.
          </p>
        </div>
      </section>

      {/* Liste par ville → pannes */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom max-w-6xl">
          {CITIES.map((city) => (
            <Card key={city.slug} className="mb-6 border-2 border-slate-100 hover:border-red-200 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-red-600" />
                      {city.name} <span className="text-base font-normal text-slate-500">({city.postalCode})</span>
                    </h2>
                    <p className="text-sm text-slate-600 mt-1">
                      {city.distanceKm} km • {city.timeMinutes} min par {city.accessRoad}
                    </p>
                  </div>
                  <Link to={`/reparation-${city.slug}`}>
                    <Button variant="outline" size="sm" className="gap-1">
                      Voir page {city.name}
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>

                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {PANNES.map((panne) => {
                    const base = getPanneBaseSlug(panne);
                    const slug = buildComboSlug(base, city.slug);
                    const short = panne.titleH1.split(" à Niort")[0].split(" - ")[0];
                    return (
                      <Link
                        key={slug}
                        to={`/${slug}`}
                        className="flex items-center justify-between bg-slate-50 hover:bg-red-50 rounded-lg px-3 py-2 text-sm text-slate-700 hover:text-red-700 transition-colors"
                        data-testid={`pv-link-${slug}`}
                      >
                        <span className="truncate pr-2">{short}</span>
                        <span className="text-xs text-slate-400 flex-shrink-0">dès {panne.priceFrom} €</span>
                      </Link>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 bg-red-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Pas de page pour votre ville ?
          </h2>
          <p className="text-red-100 mb-6 max-w-2xl mx-auto">
            Mediastore Niort propose l&apos;expédition Colissimo gratuite au-delà de 49 € pour
            toutes les villes dans un rayon de 80 km. Appelez-nous pour un diagnostic gratuit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+33549057490">
              <Button size="lg" className="bg-white text-red-600 hover:bg-red-50 gap-2">
                <Phone className="h-5 w-5" />
                05 49 05 74 90
              </Button>
            </a>
            <Link to="/click-collect">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                Click &amp; Collect
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
